import type { TaskPriority } from "@/features/tasks/lib/priority";
import { useT } from "@/shared/i18n/useT";
import { cn } from "@/shared/lib/cn";

const COLOR: Record<TaskPriority, string> = {
  urgent: "text-danger",
  high: "text-warning",
  medium: "text-ink-muted",
  low: "text-ink-subtle",
  none: "text-ink-faint",
};

/* Signal bars, shortest first: high fills all three, medium two, low one. */
const BARS = [
  { x: 1.5, y: 8.5, height: 4 },
  { x: 5.5, y: 5.5, height: 7 },
  { x: 9.5, y: 2.5, height: 10 },
] as const;
const FILLED_BARS: Partial<Record<TaskPriority, number>> = { high: 3, medium: 2, low: 1 };
const EMPTY_BAR_OPACITY = 0.22;

function Glyph({ priority }: { priority: TaskPriority }) {
  if (priority === "urgent") {
    return (
      <>
        <rect x="1" y="1" width="12" height="12" rx="3" fill="currentColor" />
        <path d="M7 4v3.6" className="stroke-surface" strokeWidth="1.7" strokeLinecap="round" />
        <circle cx="7" cy="10" r="1" className="fill-surface" />
      </>
    );
  }
  if (priority === "none") {
    return (
      <path
        d="M2 7h10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeDasharray="1.5 2.5"
      />
    );
  }
  const filled = FILLED_BARS[priority] ?? 0;
  return (
    <>
      {BARS.map((bar, index) => (
        <rect
          key={bar.x}
          x={bar.x}
          y={bar.y}
          width="3"
          height={bar.height}
          rx="1"
          fill="currentColor"
          opacity={index < filled ? 1 : EMPTY_BAR_OPACITY}
        />
      ))}
    </>
  );
}

interface PriorityIconProps {
  priority: TaskPriority;
  /** Named for screen readers ("Priority: High"); leave false next to a visible label. */
  labelled?: boolean;
  className?: string;
}

/**
 * One glyph per priority, told apart by shape as well as colour: a filled square with "!" for
 * urgent, three, two or one filled bars for high, medium and low, a dashed line for none.
 */
export function PriorityIcon({ priority, labelled = false, className }: PriorityIconProps) {
  const t = useT();
  const label = labelled ? t("priority.label", { priority: t(`priority.${priority}`) }) : undefined;
  return (
    <svg
      viewBox="0 0 14 14"
      role={labelled ? "img" : undefined}
      aria-label={label}
      aria-hidden={labelled ? undefined : true}
      className={cn("size-3.5 shrink-0", COLOR[priority], className)}
    >
      {label ? <title>{label}</title> : null}
      <Glyph priority={priority} />
    </svg>
  );
}
